import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Paper, Stack, Typography } from '@esgian/esgianui';
import { OperationsChart } from '@components/Charts';
import { DISPLAY_UNIT } from '@constants/index';
import { useDisplayUnit } from '@hooks';
import { useSelector } from 'react-redux';
import { getDisplayUnit } from '@store/features';
import { getValue, parseNumberToSignificant } from '@helpers';
import TextWithTooltipIcon from '@components/Display/TextWithTooltipIcon';
import { findOverviewSignal } from '@api/helpers';

function Operations({ loading, overviewData }) {
  const { getUnitTitle, convertUnitValue } = useDisplayUnit();
  const displayUnit = useSelector(getDisplayUnit);

  const { primaryXAxisLabel = '', series = [] } = useMemo(() => {
    if (!overviewData?.data) return {};

    const signals = overviewData.data;

    // Find signals for Accom and Drilling based on display unit
    const accomSignal = findOverviewSignal(signals, 'Accom', displayUnit);
    const drillingSignal = findOverviewSignal(signals, 'Drilling', displayUnit);

    // Either category can be missing on a partially instrumented rig - show the
    // one we have rather than blanking the whole panel.
    if (!accomSignal && !drillingSignal) return {};

    let accommodation = Object.values(accomSignal?.timeSeriesValues || {}).reduce(
      (a, b) => a + getValue(b),
      0
    );
    let drilling = Object.values(drillingSignal?.timeSeriesValues || {}).reduce(
      (a, b) => a + getValue(b),
      0
    );

    let max = accommodation > drilling ? accommodation : drilling;
    const { title, converted } = getUnitTitle(max);
    accommodation = converted
      ? convertUnitValue(accommodation, displayUnit)
      : parseNumberToSignificant(accommodation);
    drilling = converted
      ? convertUnitValue(drilling, displayUnit)
      : parseNumberToSignificant(drilling);

    return {
      primaryXAxisLabel: title,
      series: [
        {
          name: '',
          type: 'column',
          data: [accommodation, drilling]
        }
      ]
    };
  }, [overviewData, displayUnit]);

  const getSubTitle = useMemo(() => {
    switch (displayUnit) {
      case DISPLAY_UNIT.CO2:
        return 'Total CO2e emissions in period';
      case DISPLAY_UNIT.NOX:
        return 'Total NOx emissions in period';
      case DISPLAY_UNIT.FUEL:
        return 'Total fuel consumption in period';
      case DISPLAY_UNIT.ENERGY:
        return 'Total energy consumption in period';
    }
  }, [displayUnit]);

  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Stack spacing={2}>
        <Stack>
          <TextWithTooltipIcon
            tooltipText={
              'Total consumption or emissions in the selected time period for each system category.'
            }
            label={<Typography variant={'h6'}>System summary</Typography>}
          />
          <Typography variant={'body2'}>{getSubTitle}</Typography>
        </Stack>
        <Stack>
          <OperationsChart
            primaryXAxisLabel={primaryXAxisLabel}
            series={series}
            loading={loading}
          />
        </Stack>
      </Stack>
    </Paper>
  );
}

Operations.propTypes = {
  loading: PropTypes.bool,
  overviewData: PropTypes.object
};

Operations.defaultProps = {
  loading: false,
  overviewData: null
};

export default Operations;
